import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  attestClient,
  bulkAttest,
  getAttestationQueue,
  type PendingAttestationRow,
} from '../lib/advisorApi';
import { formatDate } from '../lib/format';
import './advisor.css';

const PAGE_SIZE = 20;

export function AttestationsScreen() {
  const { api } = useAuth();
  const [rows, setRows] = useState<PendingAttestationRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getAttestationQueue(api, { limit: PAGE_SIZE, offset });
      setRows(res.rows);
      setTotal(res.total);
    } catch {
      setError('We could not load the attestation queue.');
    }
  }, [api, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggle(userId: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function attestOne(userId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await attestClient(api, userId);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      await load();
    } catch {
      setError('That attestation did not go through.');
    } finally {
      setBusy(false);
    }
  }

  async function attestSelected(): Promise<void> {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      await bulkAttest(api, [...selected]);
      setSelected(new Set());
      await load();
    } catch {
      setError('Those attestations did not go through.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="la-rise">
      <header className="page__head">
        <p className="la-label">Needs you</p>
        <h1 className="page__title">Attestations</h1>
        <p className="page__sub">
          You are confirming one thing: this person is who they say they are. It is the step that
          lets them seal a message — and the reason a recipient can trust what they receive
          decades from now.
        </p>
      </header>

      {error !== null && (
        <p className="auth__alert" role="alert">
          {error}
        </p>
      )}

      {rows !== null && rows.length === 0 && (
        <div className="la-card page__empty">
          <p>Nobody is waiting. Your book is clear.</p>
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          <div className="toolbar">
            <button
              type="button"
              className="la-btn-secondary"
              disabled={busy || selected.size === 0}
              onClick={() => void attestSelected()}
            >
              Attest {selected.size > 0 ? `${selected.size} selected` : 'selected'}
            </button>
            <span className="pill">{total} waiting</span>
          </div>

          <div className="la-card tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th aria-label="Select" />
                  <th>Client</th>
                  <th>Signed up</th>
                  <th>Waiting</th>
                  <th>Verified</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.userId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.fullName}`}
                        checked={selected.has(r.userId)}
                        onChange={() => toggle(r.userId)}
                      />
                    </td>
                    <td className="table__name">
                      {r.fullName}
                      <div className="pill" style={{ marginTop: 6 }}>
                        {r.email}
                      </div>
                    </td>
                    <td>{formatDate(r.signupAt)}</td>
                    <td>
                      {r.isWaitingOver48h ? (
                        <span className="pill pill--gold">over 48h</span>
                      ) : r.isNewToday ? (
                        <span className="pill">new today</span>
                      ) : (
                        <span className="pill">{Math.round(r.ageHours)}h</span>
                      )}
                    </td>
                    <td>
                      <div className="rowActions">
                        {r.emailVerified && <span className="pill">email</span>}
                        {r.phoneVerified && <span className="pill">phone</span>}
                        {r.publicKeyRegistered && <span className="pill">passkey</span>}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="la-btn-secondary"
                        disabled={busy}
                        onClick={() => void attestOne(r.userId)}
                      >
                        Attest
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > PAGE_SIZE && (
            <div className="toolbar" style={{ marginTop: 24 }}>
              <button
                type="button"
                className="la-btn-secondary"
                disabled={busy || offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <span className="pill">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <button
                type="button"
                className="la-btn-secondary"
                disabled={busy || offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
